import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { RokezzzAuthorizationService } from 'src/app/rokezzz/user/services/authorization.service';
import { RokezzzWindowService } from 'src/app/rokezzz/window/services/window.service';
import { OnFinish } from 'src/app/rokezzz/api/api/on-finish';
import { RokezzzNotificationService } from '../../notification/services/notification.service';
import { Auth } from '../api/auth';
import { Credentials } from '../api/credentials';
import { RokezzzAuthService } from './auth.service';
import { RokezzzUserService } from './user.service';
import { RokezzzPathService } from '../../locale/services/path.service';
import { RokezzzRoleService } from './role.service';
import { Role } from '../api/role';

@Injectable({
    providedIn: 'root'
})
export class RokezzzLoginService implements OnDestroy {
    private readonly pingInterval: number = 60 * 1000;

    private pingSubscription?: Subscription;
    private document: Document;

    public constructor(
        protected readonly rokezzzAuthService: RokezzzAuthService,
        protected readonly rokezzzAuthorizationService: RokezzzAuthorizationService,
        protected readonly router: Router,
        protected readonly rokezzzNotificationService: RokezzzNotificationService,
        protected readonly rokezzzUserService: RokezzzUserService,
        protected readonly rokezzzWindowService: RokezzzWindowService,
        protected readonly rokezzzPathService: RokezzzPathService,
        protected readonly rokezzzRoleService: RokezzzRoleService,
        protected readonly ngZone: NgZone
    ) {
        this.document = this.rokezzzWindowService.getDocument();
    }

    public ngOnDestroy(): void {
        this.pingSubscription?.unsubscribe();
    }

    public async init(): Promise<void> {
        if (this.rokezzzAuthorizationService.isLoggedIn()) {
            this.rokezzzRoleService.getCollectionForUser(
                (roles: Role[]): void => {
                    this.rokezzzAuthorizationService.setRoles(roles);
                    this.startPingInterval();
                    this.ping();
                    this.rokezzzUserService.refreshLoggedUserCache();
                },
                (): void => {
                    this.logoutForced();
                }
            );
        }
    }

    public login(
        credentials: Credentials,
        onComplete: OnFinish<void>,
        onError: OnFinish<HttpErrorResponse>,
        onIncorrectCredentials: OnFinish<HttpErrorResponse>
    ): void {
        this.rokezzzAuthService.post(
            credentials,
            (auth: Auth) => {
                this.rokezzzAuthorizationService.setAuthorization(auth.token);
                this.rokezzzRoleService.getCollectionForUser(
                    (roles: Role[]) => {
                        this.rokezzzUserService.refreshLoggedUserCache();
                        this.rokezzzAuthorizationService.setRoles(roles);
                        this.rokezzzNotificationService.addSuccessNotification($localize`Pomyślnie zalogowano!`);
                        this.startPingInterval();
                        onComplete();
                    },
                    () => {
                        this.rokezzzAuthorizationService.removeAuthorization();
                        this.rokezzzNotificationService.addServerError();
                        onComplete();
                    }
                );
            },
            (error: HttpErrorResponse) => {
                if (error.status == 404 || error.status == 422 || error.status == 424) {
                    onIncorrectCredentials(error);
                } else {
                    onError(error);
                }
            }
        );
    }

    public logout(): void {
        this.rokezzzAuthService.delete(
            (): void => {
                this.logoutCommon();
                this.router.navigate([this.rokezzzPathService.getPath('home')]);
                this.rokezzzNotificationService.addSuccessNotification($localize`Pomyślnie wylogowano!`);
            },
            (error: HttpErrorResponse): void => {
                if (error.status === 404 || error.status === 401) {
                    this.logoutForced();
                } else {
                    this.rokezzzNotificationService.addServerError();
                }
            }
        );
    }

    public logoutCommon(): void {
        this.stopPingInterval();
        this.rokezzzAuthorizationService.removeAuthorization();
        this.rokezzzAuthorizationService.removeRoles();
        this.rokezzzUserService.clearLoggedUserCache();
        this.document.querySelectorAll('.overlay, .cdk-overlay-backdrop').forEach(
            (overlay: Element): void => {
                (overlay as HTMLElement).click();
            }
        );
    }

    private startPingInterval(): void {
        this.ngZone.runOutsideAngular((): void => {
            this.pingSubscription = interval(this.pingInterval).subscribe((): void => {
                this.ngZone.run((): void => this.ping());
            });
        });
    }

    private stopPingInterval(): void {
        this.pingSubscription?.unsubscribe();
    }

    private ping(): void {
        this.rokezzzAuthService.ping(
            () => { },
            () => {
                this.logoutForced();
            }
        );
    }

    private logoutForced(): void {
        this.logoutCommon();
        this.router.navigate([this.rokezzzPathService.getPath('login')]);
        this.rokezzzNotificationService.addWarningNotification($localize`Sesja wygasła! Zaloguj się ponownie.`);
    }
}
